import { createContext, useContext, useEffect, useRef, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";

// Time constants - Production: 40 min timeout, 5 min warning
const INACTIVITY_TIMEOUT = 40 * 60 * 1000; // 40 minutes
const WARNING_TIME = 5 * 60 * 1000; // Show warning 5 minutes before expiry
const CHECK_INTERVAL = 1000; // Check every 1 second

const InactivityContext = createContext(null);

export const InactivityProvider = ({ children }) => {
  const { user } = useAuth();
  const [showWarning, setShowWarning] = useState(false);
  const [showSessionExpired, setShowSessionExpired] = useState(false);
  const [remainingTime, setRemainingTime] = useState(INACTIVITY_TIMEOUT);
  
  const lastActivityRef = useRef(Date.now());
  const timeoutWarningRef = useRef(null);
  const timeoutExpiryRef = useRef(null);
  const checkIntervalRef = useRef(null);

  // Clear all timers
  const clearAllTimers = useCallback(() => {
    if (timeoutWarningRef.current) {
      clearTimeout(timeoutWarningRef.current);
      timeoutWarningRef.current = null;
    }
    if (timeoutExpiryRef.current) {
      clearTimeout(timeoutExpiryRef.current);
      timeoutExpiryRef.current = null;
    }
    if (checkIntervalRef.current) {
      clearInterval(checkIntervalRef.current);
      checkIntervalRef.current = null;
    }
  }, []);

  // Reset activity timestamp and restart timers
  const resetActivity = useCallback(() => {
    lastActivityRef.current = Date.now();
    setShowWarning(false);
    setShowSessionExpired(false);
    setRemainingTime(INACTIVITY_TIMEOUT);
    
    // Clear existing timers and restart
    clearAllTimers();
    
    if (!user) return;
    
    // Set warning timeout
    timeoutWarningRef.current = setTimeout(() => {
      setShowWarning(true);
    }, INACTIVITY_TIMEOUT - WARNING_TIME);
    
    // Set expiry timeout
    timeoutExpiryRef.current = setTimeout(() => {
      setShowSessionExpired(true);
      window.dispatchEvent(new CustomEvent("sessionExpired"));
    }, INACTIVITY_TIMEOUT);
    
    // Start interval for countdown
    checkIntervalRef.current = setInterval(() => {
      const elapsed = Date.now() - lastActivityRef.current;
      const remaining = Math.max(0, INACTIVITY_TIMEOUT - elapsed);
      setRemainingTime(remaining);
      
      // Show warning when time reaches WARNING_TIME
      if (remaining <= WARNING_TIME && remaining > 0 && !showWarning) {
        setShowWarning(true);
      }
      
      // Show session expired when time reaches 0
      if (remaining <= 0 && !showSessionExpired) {
        setShowSessionExpired(true);
        window.dispatchEvent(new CustomEvent("sessionExpired"));
      }
    }, CHECK_INTERVAL);
  }, [user, showWarning, showSessionExpired, clearAllTimers]);

  // Initialize timers on mount or when user logs in
  useEffect(() => {
    if (user) {
      resetActivity();
    }
    
    return () => clearAllTimers();
  }, [user]);

  // Set up activity listeners - resets timer even when warning modal is showing
  useEffect(() => {
    if (!user) return;
    if (showSessionExpired) return; // Don't reset if session expired modal is showing

    const activityEvents = ["mousemove", "keydown", "click", "scroll", "touchstart"];

    const handleActivity = () => {
      // Reset timer on any activity (even when warning modal is showing)
      resetActivity();
    };

    activityEvents.forEach((event) => {
      const options = event === "scroll" ? { passive: true } : {};
      window.addEventListener(event, handleActivity, options);
    });

    return () => {
      activityEvents.forEach((event) => {
        window.removeEventListener(event, handleActivity);
      });
    };
  }, [user, showSessionExpired, resetActivity]);

  // Reset state when user logs out
  useEffect(() => {
    if (!user) {
      setShowWarning(false);
      setShowSessionExpired(false);
      setRemainingTime(INACTIVITY_TIMEOUT);
      clearAllTimers();
    }
  }, [user, clearAllTimers]);

  // Handle warning modal "Stay Signed In" button
  const handleStaySignedIn = useCallback(() => {
    resetActivity();
  }, [resetActivity]);

  return (
    <InactivityContext.Provider
      value={{
        showWarning,
        showSessionExpired,
        remainingTime,
        handleStaySignedIn,
        resetActivity,
      }}
    >
      {children}
    </InactivityContext.Provider>
  );
};

export const useInactivity = () => {
  const context = useContext(InactivityContext);
  if (!context) {
    throw new Error("useInactivity must be used within InactivityProvider");
  }
  return context;
};

export default InactivityContext;
